import { supabase } from '@/lib/supabase';
import type { ChatConversation, ChatMessageRecord, ChatModel } from './types';

interface ChatModelsResponse {
  aiEnabled: boolean;
  models: ChatModel[];
}

interface ConversationsResponse {
  conversations: ChatConversation[];
}

interface ConversationResponse {
  conversation: ChatConversation;
}

interface MessagesResponse {
  messages: ChatMessageRecord[];
  pageInfo?: {
    hasMore: boolean;
    nextBefore: string | null;
  };
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw new Error(`Failed to read session: ${error.message}`);
  }

  const token = data.session?.access_token;
  if (!token) {
    throw new Error('Session expired. Please sign in again.');
  }

  return token;
}

export async function getChatAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = {
    ...(await getChatAuthHeaders()),
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers ?? {}),
  };

  const response = await fetch(path, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;

    try {
      const payload = (await response.json()) as { error?: string };
      if (payload.error) {
        message = payload.error;
      }
    } catch {
      const text = await response.text();
      if (text) {
        message = text;
      }
    }

    throw new Error(message);
  }

  return (await response.json()) as T;
}

export async function fetchChatModels(): Promise<ChatModelsResponse> {
  return requestJson<ChatModelsResponse>('/api/chat/models');
}

export async function fetchConversations(options: { includeArchived?: boolean; limit?: number } = {}): Promise<ChatConversation[]> {
  const params = new URLSearchParams();
  if (options.includeArchived) {
    params.set('includeArchived', '1');
  }
  if (options.limit) {
    params.set('limit', String(options.limit));
  }

  const query = params.toString();
  const response = await requestJson<ConversationsResponse>(`/api/chat/conversations${query ? `?${query}` : ''}`);
  return response.conversations ?? [];
}

export async function createConversation(payload: { title?: string; modelId?: string } = {}): Promise<ChatConversation> {
  const response = await requestJson<ConversationResponse>('/api/chat/conversations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  return response.conversation;
}

export async function updateConversation(
  conversationId: string,
  payload: { title?: string; modelId?: string; archived?: boolean; metadata?: Record<string, unknown> },
): Promise<ChatConversation> {
  const response = await requestJson<ConversationResponse>(`/api/chat/conversations/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

  return response.conversation;
}

export async function fetchConversationMessages(
  conversationId: string,
  options: { limit?: number; before?: string } = {},
): Promise<MessagesResponse> {
  const params = new URLSearchParams();
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  if (options.before) {
    params.set('before', options.before);
  }

  const query = params.toString();
  return requestJson<MessagesResponse>(`/api/chat/conversations/${conversationId}/messages${query ? `?${query}` : ''}`);
}
